import {
  Button,
  Container,
  Divider,
  Grid,
  Group,
  Paper,
  TextInput,
  Title,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { Check, X } from 'tabler-icons-react';
import BackButton from '../../../components/buttons/back-button.component';
import { OpenComponent } from '../../../enums/misc.enum';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { UpdatePOSsSchema } from '../../../schemas/update-pos.schema';
import { ErrorResponse } from '../../../type/error-response.types';
import { UpdatePOSsRequestFrontend } from '../../../type/pos.types';
import SkeletonUpdatePOSPage from './skeleton-loading/skeleton-update-pos.page';
import useAuth from '../../../hooks/useAuth';
import { Roles } from '../../../enums/roles-enum';

function validateSecretKet(secretKey: string): { secretKeyIsValid: boolean; errors: string } {

  if (secretKey.length < 12) {
    return {
      secretKeyIsValid: false,
      errors: "Secret Key must be at least 12 characters long."
    };
  }
  if (!/[A-Z]/.test(secretKey)) {
    return {
      secretKeyIsValid: false,
      errors: "Secret Key must contain at least one uppercase letter."
    };
  }
  if (!/[a-z]/.test(secretKey)) {
    return {
      secretKeyIsValid: false,
      errors: "Secret Key must contain at least one lowercase letter."
    };
  }
  if (!/[0-9]/.test(secretKey)) {
    return {
      secretKeyIsValid: false,
      errors: "Secret Key must contain at least one digit."
    };
  }

  return {
    secretKeyIsValid: true,
    errors: ""
  };
}

function UpdatePOSPage(props: Props) {
  const { auth } = useAuth();
  const { posId, setComponentManager, loadPOSs } = props;
  const form = useForm({
    initialValues: {
      posName: '',
      secretKey: '',
    },
    validate: joiResolver(UpdatePOSsSchema),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [initialPOSName, setInitialPOSName] = useState('');
  const [initialSecretKey, setInitialSecretKey] = useState('');
  const axiosPrivate = useAxiosPrivate();

  const labels = {
    posName: 'POS Name',
    secretKey: "Secret Key"
  };

  const placeholders = {
    posName: 'POS Name',
    secretKey: "Secret Key"
  };

  useEffect(() => {
    const fetchPOS = async () => {
      try {
        setIsInitialLoading(true);
        const response = await axiosPrivate.get(`web/pos/${posId}`);

        form.setValues({
          posName: response.data.posName,
          secretKey: response.data.secretKey
        });
        setInitialPOSName(response.data.posName);
        setInitialSecretKey(response.data.secretKey);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-fetch-pos',
          message:
            error?.error ||
            err.response?.data?.error ||
            'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchPOS();
  }, []);

  const createSecretKeySection = () => {
    return <div></div>
  }

  const handleSubmit = async () => {
    try {

      const { secretKeyIsValid, errors } = validateSecretKet(form.values.secretKey.trim());
      if (!secretKeyIsValid) {
        showNotification({
          id: 'failed-to-update-pos',
          message: errors,
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
        return;
      }

      setIsLoading(true);
      const request: UpdatePOSsRequestFrontend = {
        posName: form.values.posName.trim(),
        secretKey: form.values.secretKey.trim(),
      };

      console.log("request: ", request);

      await axiosPrivate.put(`web/pos/${posId}`, request);

      loadPOSs();
      setInitialPOSName(request.posName);
      setInitialSecretKey(request.secretKey);

      showNotification({
        id: 'successfully-update-pos',
        message: 'Successfully updated POS',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-update-pos',
        message:
          error?.error ||
          err.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onGenerateNewKey = () => {
  
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const newKey = Array.from({ length: 48 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  
    form.setFieldValue('secretKey', newKey);
  }

  const onClearKey = () => {
    form.setFieldValue('secretKey', '');
  }

  return (
    <>
      <Container fluid>
        <BackButton
          handleClick={() => {
            setComponentManager(OpenComponent.HOME);
          }}
        />
        <Title order={2} my='lg'>
          Update POS
        </Title>
        <Paper
          withBorder
          radius='md'
          shadow='lg'
          style={{
            flex: 1,
            height: '100%',
            padding: '20px 0',
          }}
        >
          <Container p='lg' fluid>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              {isInitialLoading ? (
                <SkeletonUpdatePOSPage />
              ) : (
                <Grid style={{ width: '100vw' }}>
                  <Grid.Col xs={12} sm={6}>
                    <TextInput
                      sx={{ width: 400 }}
                      placeholder={placeholders.posName}
                      label={labels.posName}
                      {...form.getInputProps('posName')}
                    />
                    {createSecretKeySection()}
                  </Grid.Col>
                </Grid>
              )}
              <Divider my='lg' />

              <Group position='right'>
                <Button
                  type='submit'
                  loading={isLoading}
                  disabled={
                    isInitialLoading ||
                    (initialPOSName === form.values.posName.trim() &&
                    initialSecretKey === form.values.secretKey.trim())
                  }
                >
                  Submit
                </Button>
              </Group>
            </form>
          </Container>
        </Paper>
      </Container>
    </>
  );
}

export default UpdatePOSPage;

interface Props {
  posId: number;
  setComponentManager: (value: React.SetStateAction<OpenComponent>) => void;
  loadPOSs: () => Promise<void>;
}
